(() => {
  const rootSelectors = [
    '[data-lulu-product-cinematic-root]',
    '[data-lulu-scratcher-root]',
  ];
  const controllers = new WeakMap();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const createController = (root) => {
    const motionSections = [...root.querySelectorAll('[data-lulu-motion]')];
    const parallaxMedia = [...root.querySelectorAll('[data-lulu-parallax]')];
    const nativeVideos = [...root.querySelectorAll('[data-lulu-motion-video]')];
    const visibleParallax = new Set();
    let frame = 0;
    let entranceObserver;
    let parallaxObserver;
    let videoObserver;
    let destroyed = false;

    if (root.matches('[data-lulu-motion]')) motionSections.unshift(root);

    return {
      motionSections,
      parallaxMedia,
      nativeVideos,
      visibleParallax,
      get frame() {
        return frame;
      },
      set frame(value) {
        frame = value;
      },
      get entranceObserver() {
        return entranceObserver;
      },
      set entranceObserver(value) {
        entranceObserver = value;
      },
      get parallaxObserver() {
        return parallaxObserver;
      },
      set parallaxObserver(value) {
        parallaxObserver = value;
      },
      get videoObserver() {
        return videoObserver;
      },
      set videoObserver(value) {
        videoObserver = value;
      },
      get destroyed() {
        return destroyed;
      },
      destroy() {
        destroyed = true;
      },
    };
  };

  const revealAll = (controller) => {
    controller.motionSections.forEach((section) => section.classList.add('is-visible'));
  };

  const resetParallax = (controller) => {
    controller.parallaxMedia.forEach((element) => {
      element.style.setProperty('--lulu-parallax-y', '0px');
    });
  };

  const initRoot = (root) => {
    if (controllers.has(root)) return;

    const controller = createController(root);
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const supportsObservers = 'IntersectionObserver' in window;
    const videoAttempts = new WeakMap();
    let videoPlaybackEnabled = false;

    const updateParallax = () => {
      controller.frame = 0;
      if (controller.destroyed || controller.visibleParallax.size === 0) return;

      const viewportHeight = window.innerHeight;
      controller.visibleParallax.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height), 0, 1);
        const direction = Number(element.dataset.luluParallax) || 1;
        const amplitude = Math.min(45, Math.max(0, viewportHeight * 0.035));
        const offset = (progress - 0.5) * 2 * amplitude * direction;
        element.style.setProperty('--lulu-parallax-y', `${offset.toFixed(2)}px`);
      });
    };

    const scheduleParallax = () => {
      if (
        controller.destroyed ||
        reducedMotionQuery.matches ||
        mobileQuery.matches ||
        !controller.parallaxObserver ||
        controller.frame ||
        controller.visibleParallax.size === 0
      ) {
        return;
      }
      controller.frame = requestAnimationFrame(updateParallax);
    };

    const stopParallax = () => {
      controller.parallaxObserver?.disconnect();
      controller.parallaxObserver = undefined;
      if (controller.frame) {
        cancelAnimationFrame(controller.frame);
        controller.frame = 0;
      }
      window.removeEventListener('scroll', scheduleParallax);
      window.removeEventListener('resize', scheduleParallax);
      controller.visibleParallax.clear();
      resetParallax(controller);
    };

    const startParallax = () => {
      if (
        controller.destroyed ||
        !supportsObservers ||
        reducedMotionQuery.matches ||
        mobileQuery.matches ||
        controller.parallaxMedia.length === 0 ||
        controller.parallaxObserver
      ) {
        return;
      }

      const parallaxObserver = new IntersectionObserver((entries) => {
        if (controller.parallaxObserver !== parallaxObserver) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            controller.visibleParallax.add(entry.target);
          } else {
            controller.visibleParallax.delete(entry.target);
            entry.target.style.setProperty('--lulu-parallax-y', '0px');
          }
        });
        scheduleParallax();
      });
      controller.parallaxObserver = parallaxObserver;
      controller.parallaxMedia.forEach((element) => parallaxObserver.observe(element));
      window.addEventListener('scroll', scheduleParallax, { passive: true });
      window.addEventListener('resize', scheduleParallax, { passive: true });
    };

    const invalidateVideoAttempt = (video) => {
      const nextAttempt = (videoAttempts.get(video) ?? 0) + 1;
      videoAttempts.set(video, nextAttempt);
      return nextAttempt;
    };

    const stopVideo = (video) => {
      invalidateVideoAttempt(video);
      video.pause();
      video.closest('[data-lulu-motion="video"]')?.classList.remove('is-playing');
    };

    const stopVideos = () => {
      videoPlaybackEnabled = false;
      controller.videoObserver?.disconnect();
      controller.videoObserver = undefined;
      controller.nativeVideos.forEach(stopVideo);
    };

    const startVideos = () => {
      if (
        controller.destroyed ||
        !supportsObservers ||
        reducedMotionQuery.matches ||
        controller.nativeVideos.length === 0 ||
        controller.videoObserver
      ) {
        return;
      }

      videoPlaybackEnabled = true;
      const videoObserver = new IntersectionObserver(
        (entries) => {
          if (controller.videoObserver !== videoObserver || !videoPlaybackEnabled) return;
          entries.forEach((entry) => {
            const video = entry.target;
            const chapter = video.closest('[data-lulu-motion="video"]');
            if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
              const attempt = invalidateVideoAttempt(video);
              const isCurrentAttempt = () =>
                videoPlaybackEnabled && !controller.destroyed && videoAttempts.get(video) === attempt;
              video.muted = true;
              let playResult;
              try {
                playResult = video.play();
              } catch {
                chapter?.classList.remove('is-playing');
                return;
              }
              if (playResult && typeof playResult.then === 'function') {
                const guardedPlay = playResult.then(() => {
                  if (isCurrentAttempt()) chapter?.classList.add('is-playing');
                });
                if (guardedPlay && typeof guardedPlay.catch === 'function') {
                  guardedPlay.catch(() => {
                    if (isCurrentAttempt()) chapter?.classList.remove('is-playing');
                  });
                } else if (typeof playResult.catch === 'function') {
                  playResult.catch(() => {
                    if (isCurrentAttempt()) chapter?.classList.remove('is-playing');
                  });
                }
              } else if (isCurrentAttempt()) {
                chapter?.classList.add('is-playing');
              }
            } else {
              stopVideo(video);
            }
          });
        },
        { threshold: [0, 0.55, 1] },
      );
      controller.videoObserver = videoObserver;
      controller.nativeVideos.forEach((video) => videoObserver.observe(video));
    };

    const handleMobileChange = () => {
      if (mobileQuery.matches) {
        stopParallax();
      } else {
        startParallax();
      }
    };

    const handleReducedMotionChange = () => {
      if (reducedMotionQuery.matches) {
        revealAll(controller);
        controller.entranceObserver?.disconnect();
        controller.entranceObserver = undefined;
        stopParallax();
        stopVideos();
      } else {
        startParallax();
        startVideos();
      }
    };

    const destroy = () => {
      controller.destroy();
      controller.entranceObserver?.disconnect();
      controller.entranceObserver = undefined;
      stopParallax();
      stopVideos();
      mobileQuery.removeEventListener('change', handleMobileChange);
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
      controllers.delete(root);
    };

    controller.destroyController = destroy;
    controllers.set(root, controller);
    mobileQuery.addEventListener('change', handleMobileChange);
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

    if (reducedMotionQuery.matches || !supportsObservers) {
      revealAll(controller);
      resetParallax(controller);
      return;
    }

    controller.entranceObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          controller.entranceObserver?.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );
    controller.motionSections.forEach((section) => controller.entranceObserver.observe(section));

    startParallax();
    if (mobileQuery.matches) resetParallax(controller);
    startVideos();
  };

  const rootsWithin = (scope) => {
    const roots = [];
    rootSelectors.forEach((rootSelector) => {
      if (scope.matches?.(rootSelector)) roots.push(scope);
      roots.push(...(scope.querySelectorAll?.(rootSelector) ?? []));
    });
    return [...new Set(roots)];
  };

  const init = (scope = document) => {
    rootsWithin(scope).forEach(initRoot);
  };

  const destroy = (root) => {
    controllers.get(root)?.destroyController?.();
  };

  document.documentElement.classList.add('lulu-scratcher-motion-ready');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = rootSelectors.reduce(
      (foundRoot, rootSelector) =>
        foundRoot ||
        (event.target.matches?.(rootSelector) && event.target) ||
        event.target.querySelector?.(rootSelector) ||
        event.target.closest?.(rootSelector),
      undefined,
    );
    if (root) destroy(root);
  });
})();
